import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { ApiSettings } from '../lib/types';
import { encryptApiKey } from '../lib/api.ts';

interface SettingsState {
  providerSettings: Record<string, ApiSettings>;
  setProviderSettings: (
    provider_id: string,
    settings: { apiKey: string; workspaceId?: string },
  ) => Promise<void>;
  removeProviderSettings: (
    provider_id: string,
    workspaceId?: string,
  ) => Promise<void>;
  loadSettings: (workspaceId?: string) => Promise<void>;
}

export const useSettingsStore = create<SettingsState>()((set) => ({
  providerSettings: {},

  loadSettings: async (workspaceId) => {
    if (!workspaceId) return;
    try {
      const { data: settings, error } = await supabase
        .from('api_settings')
        .select('id,user_id,provider_id,created_at,updated_at,workspace_id')
        .eq('workspace_id', workspaceId);

      if (error) throw error;

      const settingsMap = settings.reduce(
        (acc, setting) => {
          acc[setting.provider_id] = setting;
          return acc;
        },
        {} as Record<string, ApiSettings>,
      );

      set({ providerSettings: settingsMap });
    } catch (error) {
      console.error('Error loading API settings:', error);
    }
  },

  setProviderSettings: async (
    provider_id: string,
    settings: { apiKey: string; workspaceId?: string },
  ) => {
    try {
      // Get the current user's ID
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user || !settings.workspaceId)
        throw new Error('User is not authenticated or no workspace found');

      const { data, error } = await supabase
        .from('api_settings')
        .upsert({
          provider_id,
          user_id: user.id,
          workspace_id: settings.workspaceId,
        })
        .select('id,user_id,provider_id,created_at,updated_at,workspace_id')
        .single();

      if (error) throw error;

      try {
        await encryptApiKey({
          apiKeyId: data.id,
          apiKey: settings.apiKey,
          workspaceId: data.workspace_id,
        });

        set((state) => ({
          providerSettings: {
            ...state.providerSettings,
            [provider_id]: data,
          },
        }));
      } catch (e) {
        await supabase
          .from('api_settings')
          .delete()
          .eq('id', data.id)
          .eq('workspace_id', data.workspace_id);
      }
    } catch (error) {
      console.error('Error saving API settings:', error);
      throw error;
    }
  },

  removeProviderSettings: async (provider_id: string, workspaceId?: string) => {
    if (!workspaceId) throw new Error('No workspace found');
    try {
      const { error } = await supabase
        .from('api_settings')
        .delete()
        .eq('provider_id', provider_id)
        .eq('workspace_id', workspaceId);

      if (error) throw error;

      set((state) => {
        const newSettings = { ...state.providerSettings };
        delete newSettings[provider_id];
        return { providerSettings: newSettings };
      });
    } catch (error) {
      console.error('Error removing API settings:', error);
      throw error;
    }
  },
}));
